import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { CaretRightIcon } from "@phosphor-icons/react/dist/csr/CaretRight";
import { Fragment, useState } from "react";
import {
  ActivityFeed,
  ActivityFeedActor,
  ActivityFeedBody,
  ActivityFeedGroup,
  ActivityFeedIcon,
  ActivityFeedItem,
  ActivityFeedLine,
  ActivityFeedTime,
} from "@volca/ui/components/ui/activity-feed";
import { cn } from "@volca/ui/lib/utils";
import { dayjs } from "@/core/utils/date";
import { RecordMark } from "@/profitability/records/record-mark";
import { ValueChanges } from "@/profitability/records/value-changes";
import { activityChanges } from "./activity-changes";
import { actorName, describe } from "./activity-describe";
import type { FindingActivity, FindingHypothesis } from "./types";

const AUTOMATIC = new Set(["finding.hypothesis", "finding.flagged_again"]);
const FLAGGED = new Set([
  "finding.flagged",
  "finding.flagged_again",
  "finding.reopened",
]);

const groupOf = (at: dayjs.Dayjs) => {
  const now = dayjs();
  if (at.isSameOrAfter(now.startOf("week"))) return "This week";
  if (at.isSameOrAfter(now.subtract(1, "week").startOf("week")))
    return "Last week";
  return at.format(at.year() === now.year() ? "MMMM" : "MMMM YYYY");
};

/** One event: who (a person's initials, else what happened), what, when. */
const Event = ({
  item,
  hypothesis,
}: {
  item: FindingActivity;
  hypothesis?: FindingHypothesis | null;
}) => {
  const { tone, icon, line, body } = describe(item, hypothesis);
  const changes = activityChanges(item);
  const at = dayjs(item.occurred_at);
  // A flag that changed is the change row, so it is not also the note.
  const note = changes.some((change) => change.label === "Flag") ? null : body;
  const flagged = FLAGGED.has(item.kind) && note;
  return (
    <ActivityFeedItem>
      {item.actor ? (
        <RecordMark icon="person" name={item.actor.full_name} />
      ) : (
        <ActivityFeedIcon tone={tone}>{icon}</ActivityFeedIcon>
      )}
      <ActivityFeedLine>
        <ActivityFeedActor>{actorName(item)}</ActivityFeedActor> {line}
        <ActivityFeedTime
          dateTime={item.occurred_at}
          title={at.format("MMM D, YYYY h:mm a")}
        >
          {at.fromNow()}
        </ActivityFeedTime>
      </ActivityFeedLine>
      {/* The flag reads as the note it is, highlighted, not boxed. */}
      {(note || changes.length > 0) && (
        <ActivityFeedBody>
          {flagged && note ? (
            <span className="rounded-xs bg-warning-subtle box-decoration-clone px-1 py-0.5 text-ink">
              {note}
            </span>
          ) : (
            note
          )}
          {changes.length > 0 && (
            <ValueChanges
              changes={changes}
              className={cn({ "pt-2": !!note })}
            />
          )}
        </ActivityFeedBody>
      )}
    </ActivityFeedItem>
  );
};

/** Automatic events in a row, folded into one line until opened. */
const Folded = ({
  items,
  hypothesis,
}: {
  items: FindingActivity[];
  hypothesis?: FindingHypothesis | null;
}) => {
  const [open, setOpen] = useState(false);
  const Caret = open ? CaretDownIcon : CaretRightIcon;
  return (
    <>
      <li>
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
          className="flex h-7 items-center gap-2 rounded-md text-sm text-subtle hover:text-ink"
        >
          <Caret aria-hidden className="size-4" />
          {items.length} automatic events
        </button>
      </li>
      {open &&
        items.map((item) => (
          <Event key={item.id} item={item} hypothesis={hypothesis} />
        ))}
    </>
  );
};

/**
 * A record's whole history, newest first and grouped by when: this week, last
 * week, then by month. Automatic events in a row fold into one line; a flag
 * shows as a card under the event that raised it.
 */
export const GroupedActivityFeed = ({
  activity,
  hypothesis,
}: {
  activity: FindingActivity[];
  /** The finding's reading, which names a cause its report declared. */
  hypothesis?: FindingHypothesis | null;
}) => {
  const newest = [...activity].sort((a, b) =>
    b.occurred_at.localeCompare(a.occurred_at),
  );
  const groups = newest.reduce<{ label: string; items: FindingActivity[] }[]>(
    (all, item) => {
      const label = groupOf(dayjs(item.occurred_at));
      const last = all.at(-1);
      if (last?.label === label) last.items.push(item);
      else all.push({ label, items: [item] });
      return all;
    },
    [],
  );
  return (
    <div className="flex flex-col">
      {groups.map((group) => {
        // Runs of automatic events, each folded once it holds two or more.
        const runs = group.items.reduce<FindingActivity[][]>((all, item) => {
          const last = all.at(-1);
          const automatic = !item.actor && AUTOMATIC.has(item.kind);
          if (
            automatic &&
            last &&
            !last[0].actor &&
            AUTOMATIC.has(last[0].kind)
          )
            last.push(item);
          else all.push([item]);
          return all;
        }, []);
        return (
          <Fragment key={group.label}>
            <ActivityFeedGroup>{group.label}</ActivityFeedGroup>
            <ActivityFeed aria-label={`Activity, ${group.label.toLowerCase()}`}>
              {runs.map((run) =>
                run.length > 1 ? (
                  <Folded key={run[0].id} items={run} hypothesis={hypothesis} />
                ) : (
                  <Event
                    key={run[0].id}
                    item={run[0]}
                    hypothesis={hypothesis}
                  />
                ),
              )}
            </ActivityFeed>
          </Fragment>
        );
      })}
    </div>
  );
};
